import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Brain, Crown } from "@phosphor-icons/react";

const TIERS = [
    { tier: "entry", fee: 10, label: "Entry", desc: "Easy/medium questions" },
    { tier: "standard", fee: 50, label: "Standard", desc: "Medium difficulty" },
    { tier: "elite", fee: 100, label: "Elite", desc: "Hard mode — bigger prizes" },
];

export default function Trivia() {
    const { user, refresh } = useAuth();
    const nav = useNavigate();
    const [starting, setStarting] = useState(null);
    const [board, setBoard] = useState([]);

    useEffect(() => {
        api.get("/trivia/leaderboard").then(({ data }) => setBoard(data)).catch(() => {});
    }, []);

    const start = async (tier) => {
        if (!user) return toast.error("Log in first");
        setStarting(tier);
        try {
            const { data } = await api.post("/trivia/start", { tier });
            await refresh();
            nav(`/trivia/${data.game_id}`, { state: { game: data } });
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        } finally {
            setStarting(null);
        }
    };

    return (
        <div className="px-4 md:px-10 py-8 max-w-5xl mx-auto">
            <div className="relative rounded-sm overflow-hidden bg-wta-surface border border-wta-border mb-8">
                <img src="https://images.unsplash.com/photo-1679855928057-b7c9b0776c83" alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
                <div className="relative p-8 md:p-12">
                    <div className="font-heading text-xs tracking-[0.4em] text-wta-accent">AI TRIVIA · CLAUDE-POWERED</div>
                    <h1 className="font-heading text-4xl md:text-7xl tracking-tight uppercase mt-2 leading-none">Genius Hour</h1>
                    <p className="text-white/70 mt-3 max-w-xl">20 questions. 10 seconds each. Answer faster, score higher.</p>
                </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-12">
                {TIERS.map((t) => (
                    <button key={t.tier} onClick={() => start(t.tier)} disabled={starting !== null} data-testid={`trivia-tier-${t.tier}`} className="text-left bg-wta-surface border border-wta-border p-6 rounded-sm hover:border-wta-primary transition disabled:opacity-50">
                        <div className="flex items-center justify-between">
                            <div className="font-heading text-3xl tracking-wide">{t.label}</div>
                            <Brain size={28} className="text-wta-muted" />
                        </div>
                        <div className="font-heading text-5xl mt-3">${t.fee}</div>
                        <div className="text-xs text-wta-muted tracking-widest uppercase mt-1">{t.fee * 100} coins</div>
                        <div className="text-sm text-white/70 mt-3">{t.desc}</div>
                        <div className="mt-4 inline-block btn-primary px-5 py-2 rounded-sm text-sm">{starting === t.tier ? "Starting…" : "Play"}</div>
                    </button>
                ))}
            </div>

            <div className="font-heading text-3xl tracking-wider uppercase mb-4 flex items-center gap-2">
                <Crown size={24} weight="fill" className="text-wta-accent" /> Top Trivia Scores
            </div>
            <div className="bg-wta-surface border border-wta-border rounded-sm overflow-hidden">
                {board.length === 0 ? (
                    <div className="p-6 text-wta-muted text-center text-sm">No completed games yet — be the first.</div>
                ) : (
                    board.map((g, i) => (
                        <div key={g.id} className={`flex items-center gap-4 px-5 py-3 ${i !== board.length - 1 ? "border-b border-wta-border" : ""}`}>
                            <div className="font-heading text-2xl w-8">{i + 1}</div>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm truncate">@{g.user?.name || "—"}</div>
                                <div className="text-xs text-wta-muted">{g.tier} tier</div>
                            </div>
                            <div className="font-heading text-2xl text-wta-accent">{g.score}</div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
